import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../Apis/apiCall";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import { usePermissions } from "../context/PermissionContext";
import Breadcrumb from "../components/Breadcrumb";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import LoadingComponent from "../components/ui/LoadingComponent";

const EditCountryPage = () => {
  const [countryName, setCountryName] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [currencyCode, setCurrencyCode] = useState("");
  const [currencySymbol, setCurrencySymbol] = useState("");
  const [timezone, setTimezone] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  
  const navigate = useNavigate();
  const { countryId } = useParams();
  const { hasPermission } = usePermissions();

  // Fetch country data on component mount
  useEffect(() => {
    fetchCountryData();
  }, [countryId]);

  const fetchCountryData = async () => {
    try {
      setPageLoading(true);
      setErrorMessage("");

      // Use the same API as your AllCountries component to get all countries
      // then find the specific country by ID
      const response = await apiCall(
        "get",
        "/get-all-countries",
        null,
        navigate
      );

      if (response?.data?.success) {
        const countryList = response.data.data || [];
        const country = countryList.find(c => c.country_id == countryId);
        
        if (country) {
          setCountryName(country.country_name || "");
          setCountryCode(country.country_code || "");
          setCurrencyCode(country.currency_code || "");
          setCurrencySymbol(country.currency_symbol || "");
          setTimezone(country.timezone || "");
          
          const handleRes = response.data;
          handleApiResponse(handleRes, "success", "GET");
        } else {
          setErrorMessage("Country not found.");
          handleCustomResponse("error", "Country not found.");
        }
      } else {
        const errorMsg = response?.data?.message || "Failed to load country data.";
        setErrorMessage(errorMsg);
        handleCustomResponse("error", "Failed to load country data.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
      const errorMsg = error?.response?.data?.message || "Error fetching country data.";
      setErrorMessage(errorMsg);
    } finally {
      setPageLoading(false);
    }
  };

  const handleCountryCodeChange = (e) => {
    setCountryCode(e.target.value.toUpperCase());
  };

  const handleCurrencyCodeChange = (e) => {
    setCurrencyCode(e.target.value.toUpperCase());
  };

  const handleCurrencySymbolChange = (e) => {
    setCurrencySymbol(e.target.value);
  };

  const validateFields = () => {
    if (!countryName.trim()) {
      handleCustomResponse("error", "Country name is required.");
      setErrorMessage("Country name is required.");
      return false;
    }
    return true;
  };

  const isFormValid = () => {
    return countryName.trim() !== "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFields()) {
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      const payload = {
        country_name: countryName,
        country_code: countryCode || null,
        currency_code: currencyCode || null,
        currency_symbol: currencySymbol || null,
        timezone: timezone || null,
      };

      const response = await apiCall(
        "put",
        `/update-country/${countryId}`,
        payload,
        navigate
      );

      if (response?.data?.success) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "PUT");
        
        // Navigate back after successful submission
        setTimeout(() => {
          navigate("/all-countries");
        }, 1000);
      } else {
        const errorMsg = response?.data?.message || "Failed to update country.";
        setErrorMessage(errorMsg);
        handleCustomResponse("error", "Failed to update country.");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "PUT");
      const errorMsg = error?.response?.data?.message || "Error updating country.";
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <LoadingComponent />
      </div>
    );
  }

  if (!hasPermission("update_countries")) {
    return (
      <div className="p-4">
        <div className="alert alert-danger">
          You do not have permission to edit countries.
        </div>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb title="Edit Country" />

      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="row g-20">
              <div className="col-md-6 mb-20">
                <label htmlFor="countryName" className="form-label">
                  Country Name *
                </label>
                <input
                  id="countryName"
                  type="text"
                  className="form-control radius-8"
                  value={countryName}
                  onChange={(e) => setCountryName(e.target.value)}
                  placeholder="Enter country name"
                  disabled={loading}
                />
              </div>

              <div className="col-md-6 mb-20">
                <label htmlFor="countryCode" className="form-label">
                  ISO Country Code
                </label>
                <input
                  id="countryCode"
                  type="text"
                  className="form-control radius-8"
                  value={countryCode}
                  onChange={handleCountryCodeChange}
                  placeholder="Enter ISO country code (e.g., PK)"
                  maxLength="2"
                  style={{ textTransform: "uppercase" }}
                  disabled={loading}
                />
              </div>

              <div className="col-md-6 mb-20">
                <label htmlFor="currencyCode" className="form-label">
                  Currency Code
                </label>
                <input
                  id="currencyCode"
                  type="text"
                  className="form-control radius-8"
                  value={currencyCode}
                  onChange={handleCurrencyCodeChange}
                  placeholder="Enter currency code (e.g., PKR)"
                  maxLength="3"
                  style={{ textTransform: "uppercase" }}
                  disabled={loading}
                />
              </div>

              <div className="col-md-6 mb-20">
                <label htmlFor="currencySymbol" className="form-label">
                  Currency Symbol
                </label>
                <input
                  id="currencySymbol"
                  type="text"
                  className="form-control radius-8"
                  value={currencySymbol}
                  onChange={handleCurrencySymbolChange}
                  placeholder="Enter currency symbol (e.g., ₨)"
                  maxLength="5"
                  disabled={loading}
                />
              </div>

              <div className="col-md-6 mb-20">
                <label htmlFor="timezone" className="form-label">
                  Timezone
                </label>
                <input
                  id="timezone"
                  type="text"
                  className="form-control radius-8"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  placeholder="Enter timezone (e.g., Asia/Karachi)"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="d-flex justify-content-center gap-3">
              <CancelButtonForm />
              <UpdateButtonForm 
                disabled={loading || !isFormValid()}
                text={loading ? "Please wait..." : "Update Country"}
              />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditCountryPage;